/**
 * AdminUtilizationBar — ratio health bar for admin (BudgetForge-inspired).
 * Black-glass; emerald / amber / red by threshold. No division-by-zero.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { AdminSkeleton } from "@/components/admin/AdminStates";

export type UtilizationTone = "auto" | "good" | "warn" | "bad" | "neutral";

export interface AdminUtilizationBarProps {
  /** Amount used / committed. */
  value: number;
  /** Capacity / total / target. If ≤0 → empty/neutral (no NaN %). */
  max: number;
  label?: ReactNode;
  /** Secondary line under the bar. */
  hint?: ReactNode;
  /** Force color; default auto by % (≤70 good, ≤90 warn, else bad). Invert for "lower is better" via invert. */
  tone?: UtilizationTone;
  /** When true, high % is good (e.g. checks OK). Default false (budget: high = bad). */
  higherIsBetter?: boolean;
  loading?: boolean;
  /** Cap display width at 100% even if value > max. */
  clamp?: boolean;
  className?: string;
  /** Show "x / y (z%)" on the right. */
  showMeta?: boolean;
  formatValue?: (n: number) => string;
}

function resolveTone(pct: number | null, tone: UtilizationTone, higherIsBetter: boolean): Exclude<UtilizationTone, "auto"> {
  if (tone !== "auto") return tone;
  if (pct == null || !Number.isFinite(pct)) return "neutral";
  if (higherIsBetter) {
    if (pct >= 90) return "good";
    if (pct >= 60) return "warn";
    return "bad";
  }
  if (pct <= 70) return "good";
  if (pct <= 90) return "warn";
  return "bad";
}

const FILL: Record<Exclude<UtilizationTone, "auto">, string> = {
  good: "bg-emerald-500/80",
  warn: "bg-amber-500/80",
  bad: "bg-red-500/80",
  neutral: "bg-white/25",
};

export function AdminUtilizationBar({
  value,
  max,
  label,
  hint,
  tone = "auto",
  higherIsBetter = false,
  loading = false,
  clamp = true,
  className,
  showMeta = true,
  formatValue = (n) =>
    Number.isFinite(n)
      ? new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(n)
      : "—",
}: AdminUtilizationBarProps) {
  const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
  const safeMax = Number.isFinite(max) && max > 0 ? max : 0;
  const pct = safeMax > 0 ? (safeValue / safeMax) * 100 : null;
  const widthPct = pct == null ? 0 : clamp ? Math.min(100, pct) : Math.max(0, pct);
  const resolved = resolveTone(pct, tone, higherIsBetter);
  const over = pct != null && pct > 100;

  if (loading) {
    return (
      <div className={cn("admin-utilization space-y-2", className)}>
        {label && <AdminSkeleton className="h-3 w-32 rounded" />}
        <AdminSkeleton className="h-2 w-full rounded-full" />
      </div>
    );
  }

  return (
    <div className={cn("admin-utilization space-y-2", className)}>
      {(label != null || showMeta) && (
        <div className="flex items-baseline justify-between gap-3 text-[12px]">
          {label != null ? (
            <div className="min-w-0 font-medium text-white/70">{label}</div>
          ) : (
            <span />
          )}
          {showMeta && (
            <div className="shrink-0 tabular-nums text-white/45">
              {safeMax > 0 ? (
                <>
                  {formatValue(safeValue)} / {formatValue(safeMax)}
                  {pct != null && (
                    <span className={cn("ml-1.5 font-semibold", over ? "text-red-300" : "text-white/60")}>
                      ({pct.toFixed(0)}%)
                    </span>
                  )}
                </>
              ) : (
                <span className="text-white/30">sin base</span>
              )}
            </div>
          )}
        </div>
      )}
      <div
        className="admin-utilization-track h-2 overflow-hidden rounded-full bg-white/[0.06] ring-1 ring-inset ring-white/[0.06]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeMax > 0 ? Math.round(safeMax) : 100}
        aria-valuenow={Math.round(safeValue)}
        aria-label={typeof label === "string" ? label : "Utilización"}
      >
        <div
          className={cn(
            "admin-utilization-fill h-full rounded-full transition-[width] duration-300 ease-out",
            FILL[resolved],
            safeMax <= 0 && "opacity-30",
          )}
          style={{ width: `${widthPct}%` }}
        />
      </div>
      {hint != null && <div className="text-[11px] text-white/35">{hint}</div>}
      {over && (
        <div className="text-[11px] font-medium text-red-300/90">
          Por encima de la base (+{(pct! - 100).toFixed(0)}%)
        </div>
      )}
    </div>
  );
}
